import API_CONSTANTS from '@/utils/apiConstants';
import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from './customBaseQuery';

const baseUrl = process.env.NEXT_PUBLIC_API_URL.replace(/\/+$/, '');

// Shared link helpers moved to '@/utils/shareLinks'

export const propertyApi = createApi({
  reducerPath: 'propertyApi',
  baseQuery: customBaseQuery({ baseUrl }),
  tagTypes: ['Property', 'PropertyList', 'PropertyDetails', 'PropertyDocuments'],
  endpoints: (builder) => ({
    createProperty: builder.mutation({
      query: (propertyData) => ({
        url: API_CONSTANTS.PROPERTY.ADD_PROPERTY,
        method: 'POST',
        body: propertyData,
      }),
      invalidatesTags: ['PropertyList'],
    }),
    createSellingFinancial: builder.mutation({
      query: ({ propertyId, financialData }) => ({
        url: API_CONSTANTS.PROPERTY.ADD_FINANCIAL_SALE.replace(':id', propertyId),
        method: 'POST',
        body: financialData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),
    createRentingFinancial: builder.mutation({
      query: ({ propertyId, financialData }) => ({
        url: API_CONSTANTS.PROPERTY.ADD_FINANCIAL_RENT.replace(':id', propertyId),
        method: 'POST',
        body: financialData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),
    savePropertyDetails: builder.mutation({
      query: ({ propertyId, details }) => ({
        url: API_CONSTANTS.PROPERTY.SAVE_PROPERTY_DETAILS.replace(':id', propertyId),
        method: 'POST',
        body: details,
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'PropertyDetails', id: propertyId }, 'PropertyList'],
    }),
    createUnitFeatures: builder.mutation({
      query: ({ propertyId, unitFeaturesData }) => ({
        url: API_CONSTANTS.PROPERTY.ADD_UNIT_FEATURES.replace(':id', propertyId),
        method: 'POST',
        body: unitFeaturesData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),
    addMedia: builder.mutation({
      query: (formData) => ({
        url: API_CONSTANTS.PROPERTY.ADD_MEDIA,
        method: 'POST',
        body: formData,
        // Let the browser set the Content-Type with boundary
        prepareHeaders: (headers) => {
          headers.delete('Content-Type');
          return headers;
        },
      }),
      invalidatesTags: (result, error, formData) => {
        const propertyId = formData.get('propertyId');
        return propertyId
          ? [
              { type: 'Property', id: propertyId },
              { type: 'PropertyDetails', id: propertyId },
            ]
          : ['Property'];
      },
    }),
    addPropertyDocument: builder.mutation({
      query: ({ propertyId, documentData }) => ({
        url: API_CONSTANTS.PROPERTY.ADD_DOCUMENT.replace(':id', propertyId),
        method: 'POST',
        body: documentData,
        prepareHeaders: (headers) => {
          headers.delete('Content-Type');
          return headers;
        },
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'PropertyDocuments', id: propertyId }],
    }),
    updatePropertyDocument: builder.mutation({
      query: ({ propertyId, documentId, documentData }) => ({
        url: API_CONSTANTS.PROPERTY.UPDATE_DOCUMENT.replace(':id', propertyId).replace(':documentId', documentId),
        method: 'PUT',
        body: documentData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'PropertyDocuments', id: propertyId }],
    }),
    deletePropertyDocument: builder.mutation({
      query: ({ propertyId, documentId }) => ({
        url: API_CONSTANTS.PROPERTY.DELETE_DOCUMENT.replace(':id', propertyId).replace(':documentId', documentId),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'PropertyDocuments', id: propertyId }],
    }),
    uploadMandateDocument: builder.mutation({
      query: ({ propertyId, payload }) => ({
        url: API_CONSTANTS.PROPERTY.UPLOAD_MANDATE_DOCUMENT.replace(':id', propertyId),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'PropertyDocuments', id: propertyId },
        { type: 'Property', id: propertyId },
      ],
    }),

    createESign: builder.mutation({
      query: (payload) => ({
        url: API_CONSTANTS.E_SIGN_CREATE,
        method: 'POST',
        body: payload,
      }),
    }),

    getPropertyBasicById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_BASIC.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),
    getAllPropertyDocuments: builder.query({
      query: ({ propertyId, search }) => {
        const params = new URLSearchParams();
        if (search) {
          params.append('search', search);
        }
        return {
          url: `${API_CONSTANTS.PROPERTY.GET_ALL_PROPERTIES_DOCUMENTS.replace(':id', propertyId)}${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { propertyId }) => [{ type: 'PropertyDocuments', id: propertyId }],
    }),
    saveLegalDetails: builder.mutation({
      query: ({ propertyId, legalData }) => ({
        url: API_CONSTANTS.PROPERTY.SAVE_LEGAL_DETAILS.replace(':id', propertyId),
        method: 'POST',
        body: legalData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    updateLegalDetails: builder.mutation({
      query: ({ propertyId, legalData }) => ({
        url: API_CONSTANTS.PROPERTY.SAVE_LEGAL_DETAILS.replace(':id', propertyId),
        method: 'PUT',
        body: legalData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),

    getPropertyLegalById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_LEGAL.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),

    updatePropertyBasicById: builder.mutation({
      query: ({ propertyId, formData }) => ({
        url: API_CONSTANTS.PROPERTY.UPDATE_PROPERTY_BASIC.replace(':id', propertyId),
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'PropertyDetails', id: propertyId }, 'PropertyList'],
    }),

    getPropertyFinanceById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_FINANCE.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }, 'PropertyList'],
    }),

    createBuyer: builder.mutation({
      query: (buyerData) => ({
        url: API_CONSTANTS.CREATE_BUYER,
        method: 'POST',
        body: buyerData,
      }),
    }),

    getPropertyDetailsById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_DETAILS.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'PropertyDetails', id: propertyId }],
    }),
    getAllProperties: builder.query({
      query: ({ filterType = 'ALL', pageNumber = 0, pageSize = 8, search = '', ...filterFields }) => {
        const params = {
          filterType,
          pageNumber,
          pageSize,
          ...(search?.trim() && { search: search.trim() }),
        };

        const cleanedBody = Object.entries(filterFields).reduce((acc, [key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {});

        return {
          url: API_CONSTANTS.PROPERTY.GET_ALL_PROPERTIES,
          method: 'POST',
          params,
          body: cleanedBody,
        };
      },
      providesTags: ['PropertyList'],
    }),
    getUnitFeaturesById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_UNIT_FEATURES.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),
    getUnitFeaturesMetadata: builder.query({
      query: () => ({
        url: API_CONSTANTS.PROPERTY.GET_UNIT_FEATURES_METADATA,
        method: 'GET',
      }),
    }),
    getPropertyMediaContentById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_MEDIA_CONTENT.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),
    getPropertyById: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_BY_ID.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [
        { type: 'Property', id: propertyId },
        { type: 'PropertyDetails', id: propertyId },
      ],
    }),
    getBuyerSharedProperties: builder.query({
      query: ({ shareId, page = 0, size = 20 }) => ({
        url: API_CONSTANTS.BUYER.GET_SHARED_PROPERTIES.replace(':shareId', shareId),
        method: 'GET',
        params: { page, size },
      }),
      providesTags: ['PropertyList'],
    }),
    shareBuyerProperties: builder.mutation({
      query: ({ propertyIds }) => ({
        url: API_CONSTANTS.BUYER.SHARE_PROPERTIES,
        method: 'POST',
        body: { propertyIds },
      }),
    }),
    getSharedProperty: builder.query({
      query: ({ brokerId, propertyId }) => ({
        url: API_CONSTANTS.COMMON.SHARE_PROPERTY.replace(':brokerId', brokerId).replace(':propertyId', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, { propertyId }) => [{ type: 'Property', id: propertyId }],
    }),
    getPropertyMedia: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PROPERTY_MEDIA.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),
    getMatchedProperties: builder.query({
      query: ({ buyerId, page = 0, size = 8, sortBy = 'projectTitle', sortDirection = 'ASC', filterType = 'ALL', availableFor, myListings }) => {
        const params = new URLSearchParams();
        // Add pagination parameters
        params.append('page', page.toString());
        params.append('size', size.toString());
        params.append('sortBy', sortBy);
        params.append('sortDirection', sortDirection);

        // Add filter parameters
        if (filterType && filterType !== 'ALL') {
          params.append('propertyType', filterType);
        }
        if (availableFor) {
          params.append('availableFor', availableFor);
        }
        if (myListings) {
          params.append('myListings', 'true');
        }

        const url = `${API_CONSTANTS.BUYER.GET_MATCHED_PROPERTIES.replace(':id', buyerId)}?${params.toString()}`;
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['PropertyList'],
    }),

    updatePropertyStatus: builder.mutation({
      query: ({ propertyId, statusData }) => ({
        url: API_CONSTANTS.PROPERTY.PUBLISH_PROPERTY.replace(':id', propertyId),
        method: 'POST',
        body: statusData,
      }),
      invalidatesTags: (result, error, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'PropertyDetails', id: propertyId }, 'PropertyList'],
    }),
    getPublishProperty: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.PROPERTY.GET_PUBLISHED_PROPERTIES.replace(':id', propertyId),
        method: 'GET',
      }),
      providesTags: (result, error, propertyId) => [{ type: 'Property', id: propertyId }],
    }),
    getExclusivePropertyVisibility: builder.query({
      query: (exclusivePropertyId) => ({
        url: API_CONSTANTS.EXCLUSIVE_PROPERTY_VISIBILITY.replace(':exclusivePropertyId', exclusivePropertyId),
        method: 'GET',
      }),
      providesTags: (result, error, exclusivePropertyId) => [{ type: 'Property', id: exclusivePropertyId }],
    }),

    // Download property mandate sample document (base64) for a given propertyId
    getPropertyMandateDocument: builder.query({
      query: (propertyId) => ({
        url: API_CONSTANTS.GET_PROPERTY_MANDATE_DOCUMENT.replace(':propertyId', propertyId),
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useCreatePropertyMutation,
  useCreateSellingFinancialMutation,
  useCreateRentingFinancialMutation,
  useSavePropertyDetailsMutation,
  useCreateUnitFeaturesMutation,
  useAddMediaMutation,
  useGetPropertyBasicByIdQuery,
  useAddPropertyDocumentMutation,
  useUpdatePropertyDocumentMutation,
  useDeletePropertyDocumentMutation,
  useUploadMandateDocumentMutation,
  useUpdatePropertyBasicByIdMutation,
  useGetAllPropertyDocumentsQuery,
  useSaveLegalDetailsMutation,
  useUpdateLegalDetailsMutation,
  useGetPropertyLegalByIdQuery,
  useGetPropertyFinanceByIdQuery,
  useCreateBuyerMutation,
  useGetPropertyDetailsByIdQuery,
  useLazyGetAllPropertiesQuery,
  useGetUnitFeaturesByIdQuery,
  useGetUnitFeaturesMetadataQuery,
  useGetPropertyMediaContentByIdQuery,
  useGetPropertyByIdQuery,
  useGetBuyerSharedPropertiesQuery,
  useLazyGetBuyerSharedPropertiesQuery,
  useShareBuyerPropertiesMutation,
  useGetSharedPropertyQuery,
  useGetPropertyMediaQuery,
  useGetMatchedPropertiesQuery,
  useUpdatePropertyStatusMutation,
  useGetPublishPropertyQuery,
  useGetExclusivePropertyVisibilityQuery,
  useGetPropertyMandateDocumentQuery,
  useCreateESignMutation,
} = propertyApi;
